import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "mantine-react-table/styles.css";
import "./mantine-overrides.css";

import {
  Anchor,
  createTheme,
  DEFAULT_THEME,
  DefaultMantineColor,
  Drawer,
  Highlight,
  Input,
  MantineColorsTuple,
  MantineProvider,
  MultiSelect,
  Paper,
  Select,
} from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";

import "dayjs/locale/nb";

import { DateInput } from "@mantine/dates";
import dayjs from "dayjs";

import DatesProvider from "./mantine-dates-provider";

const theme = createTheme({
  fontFamily: `Ubuntu, ${DEFAULT_THEME.fontFamily}`,
  headings: {
    fontFamily: `Ubuntu, ${DEFAULT_THEME.fontFamily}`,
  },
  colors: {
    primary: [
      "#F0F3F9",
      "#DFE2ED",
      "#BBC2DB",
      "#94A1CA",
      "#7484BC",
      "#5F72B4",
      "#5469B2",
      "#46599C",
      "#3D4F8D",
      "#31447D",
    ],
  },
  primaryColor: "primary",
  components: {
    Input: Input.extend({
      // https://mantine.dev/core/input/?t=styles-api
      // @ts-expect-error --input-bd is not officially supported Input CSS variables, but works
      vars: (_theme, props) => {
        // props.variant === "unstyled" is used by Table filters and in general we shouldn't override it
        // @ts-expect-error readOnly is not officially supported in InputProps, but it's being passed down from parent components like TextInput or Select
        if (!props.readOnly && props.variant !== "unstyled") {
          return {
            wrapper: {
              "--input-bd": "var(--mantine-color-primary-4)",
            },
          };
        }

        return { wrapper: {} };
      },
    }),
    InputWrapper: Input.Wrapper.extend({
      defaultProps: {
        inputWrapperOrder: ["label", "input", "description", "error"],
      },
    }),
    Paper: Paper.extend({
      defaultProps: {
        shadow: "xs",
        withBorder: true,
      },
    }),
    DateInput: DateInput.extend({
      defaultProps: {
        valueFormat: "L",
        dateParser: (val: string) => {
          if (!val) {
            return null;
          }
          return dayjs(val, ["L", "DD MM"]).toDate();
        },
        allowDeselect: false,
        // sortof simulates confirming the date
        onKeyUp: (e) => {
          if (e.key === "Enter") {
            e.currentTarget.blur();
          }
        },
      },
    }),
    Anchor: Anchor.extend({
      defaultProps: {
        size: "sm",
      },
    }),
    Select: Select.extend({
      defaultProps: {
        selectFirstOptionOnChange: true,
      },
    }),
    MultiSelect: MultiSelect.extend({
      defaultProps: {
        selectFirstOptionOnChange: true,
      },
    }),
    Highlight: Highlight.extend({
      defaultProps: {
        size: "sm",
      },
    }),
    Drawer: Drawer.extend({
      defaultProps: {
        styles: {
          content: {
            backgroundColor: "white",
          },
          header: {
            backgroundColor: "inherit",
          },
        },
      },
    }),
  },
});

export default function MantineProviderWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <MantineProvider theme={theme}>
      <DatesProvider>
        <ModalsProvider>
          <Notifications />
          {children}
        </ModalsProvider>
      </DatesProvider>
    </MantineProvider>
  );
}

type ExtendedCustomColors = "primary" | DefaultMantineColor;

declare module "@mantine/core" {
  export interface MantineThemeColorsOverride {
    colors: Record<ExtendedCustomColors, MantineColorsTuple>;
  }
}
